import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowSquareOut } from "@phosphor-icons/react/dist/icons/ArrowSquareOut";
import { Quotes } from "@phosphor-icons/react/dist/icons/Quotes";
import { paths } from "config/paths";
import Button from "@mui/material/Button";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
// Import the one style we use directly, not the `styles/prism` barrel: that barrel
// re-exports all 44 styles with extensionless relative paths, which Node's ESM
// resolver rejects when `next build` externalises the package for the server bundle.
import oneDark from "react-syntax-highlighter/dist/cjs/styles/prism/one-dark";
import type { MDXRemoteProps } from "next-mdx-remote";

import { headingFontFamily } from "../heading-font";

/**
 * A pull-quote for emphasising a line mid-article. Authored in MDX as:
 * `<PullQuote author="Prof. Joyce Slater">The number one challenge…</PullQuote>`
 */
export function PullQuote({
	children,
	author,
}: {
	children: ReactNode;
	author?: string;
}) {
	return (
		<Box
			component="figure"
			sx={{
				my: 5,
				mx: 0,
				pl: 3,
				borderLeft: "4px solid var(--mui-palette-primary-main)",
			}}>
			<Quotes
				size={28}
				weight="fill"
				color="var(--mui-palette-primary-main)"
			/>
			<Typography
				component="blockquote"
				sx={{
					fontFamily: headingFontFamily,
					fontSize: "1.5rem",
					lineHeight: 1.4,
					fontWeight: 500,
					m: 0,
					mt: 1,
				}}>
				{children}
			</Typography>
			{author ? (
				<Typography
					component="figcaption"
					color="text.secondary"
					sx={{ mt: 1.5 }}>
					— {author}
				</Typography>
			) : null}
		</Box>
	);
}

/**
 * A screenshot that links out to a live Mango map — the recurring case-study
 * pattern. Authored as:
 * `<EmbeddedMap src="/app-assets/images/articles/<slug>/map.png" href="https://…" caption="…" />`
 */
export function EmbeddedMap({
	src,
	href,
	caption,
	alt,
}: {
	src: string;
	href: string;
	caption?: string;
	alt?: string;
}) {
	return (
		<Box
			component="figure"
			sx={{ my: 5, mx: 0 }}>
			<Link
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				sx={{
					display: "block",
					position: "relative",
					borderRadius: 2,
					overflow: "hidden",
					boxShadow: "var(--mui-shadows-8)",
					"&:hover .article-embedded-map__overlay": { opacity: 1 },
				}}>
				<Box
					component="img"
					src={src}
					alt={alt ?? caption ?? "Explore the live map"}
					sx={{ display: "block", width: "100%", height: "auto" }}
				/>
				<Stack
					className="article-embedded-map__overlay"
					direction="row"
					spacing={1}
					sx={{
						position: "absolute",
						inset: 0,
						alignItems: "center",
						justifyContent: "center",
						color: "var(--mui-palette-common-white)",
						bgcolor: "rgba(0,0,0,0.35)",
						opacity: 0,
						transition: "opacity 150ms ease",
					}}>
					<ArrowSquareOut
						size={22}
						weight="bold"
					/>
					<Typography sx={{ color: "inherit", fontWeight: 600 }}>
						Explore the live map
					</Typography>
				</Stack>
			</Link>
			{caption ? (
				<Typography
					component="figcaption"
					color="text.secondary"
					variant="body2"
					sx={{ mt: 1, textAlign: "center" }}>
					{caption}
				</Typography>
			) : null}
		</Box>
	);
}

/** Inline call-to-action button, e.g. `<Cta label="Start your free trial" />`. */
export function Cta({
	label = "Start your free trial",
	href = paths.signUp,
}: {
	label?: string;
	href?: string;
}) {
	return (
		<Box sx={{ my: 4 }}>
			<Button
				href={href}
				variant="contained"
				size="large">
				{label}
			</Button>
		</Box>
	);
}

function CodeBlock({
	className,
	children,
}: {
	className?: string;
	children?: ReactNode;
}) {
	const language = /language-(\w+)/.exec(className ?? "")?.[1];
	const code = String(children).replace(/\n$/, "");

	// Inline code (no language / no newline) renders as a simple styled span.
	if (!language) {
		return (
			<Box
				component="code"
				sx={{
					px: 0.75,
					py: 0.25,
					borderRadius: 0.75,
					fontSize: "0.875em",
					bgcolor: "var(--mui-palette-background-level2)",
					fontFamily: "var(--mui-fontFamily-monospace, monospace)",
				}}>
				{children}
			</Box>
		);
	}

	return (
		<Box
			sx={{ my: 3, borderRadius: 2, overflow: "hidden", fontSize: "0.9rem" }}>
			<SyntaxHighlighter
				language={language}
				style={oneDark}
				customStyle={{ margin: 0 }}>
				{code}
			</SyntaxHighlighter>
		</Box>
	);
}

/**
 * The tag → component map handed to `<MDXRemote>`. Standard markdown elements
 * get MUI styling; the custom components above are exposed by name to authors.
 */
interface HeadingProps {
	children?: ReactNode;
	/**
	 * Unset for articles. The legal map passes a slug so that clauses can be
	 * deep-linked — legal docs cross-reference their own sections, and
	 * procurement teams link to a specific one.
	 */
	id?: string;
}

export function ArticleH2({ children, id }: HeadingProps) {
	return (
		<Typography
			component="h2"
			id={id}
			sx={{
				fontFamily: headingFontFamily,
				fontSize: "1.75rem",
				fontWeight: 600,
				lineHeight: 1.3,
				mt: 6,
				mb: 2,
			}}>
			{children}
		</Typography>
	);
}

export function ArticleH3({ children, id }: HeadingProps) {
	return (
		<Typography
			component="h3"
			id={id}
			sx={{
				fontFamily: headingFontFamily,
				fontSize: "1.35rem",
				fontWeight: 600,
				lineHeight: 1.3,
				mt: 4,
				mb: 1.5,
			}}>
			{children}
		</Typography>
	);
}

export const articleMdxComponents: MDXRemoteProps["components"] = {
	h2: ArticleH2,
	h3: ArticleH3,
	p: ({ children }) => (
		<Typography
			component="p"
			sx={{ fontSize: "1.0625rem", lineHeight: 1.7, my: 2.5 }}>
			{children}
		</Typography>
	),
	a: ({ children, href }) => (
		<Link
			href={href}
			sx={{ color: "var(--mui-palette-primary-main)" }}>
			{children}
		</Link>
	),
	ul: ({ children }) => (
		<Box
			component="ul"
			sx={{ pl: 3, my: 2.5, fontSize: "1.0625rem", lineHeight: 1.7 }}>
			{children}
		</Box>
	),
	ol: ({ children }) => (
		<Box
			component="ol"
			sx={{ pl: 3, my: 2.5, fontSize: "1.0625rem", lineHeight: 1.7 }}>
			{children}
		</Box>
	),
	li: ({ children }) => (
		<Box
			component="li"
			sx={{ mb: 1 }}>
			{children}
		</Box>
	),
	blockquote: ({ children }) => (
		<Box
			component="blockquote"
			sx={{
				my: 4,
				mx: 0,
				pl: 3,
				borderLeft: "4px solid var(--mui-palette-divider)",
				color: "text.secondary",
				fontStyle: "italic",
			}}>
			{children}
		</Box>
	),
	img: ({ src, alt }) => (
		<Box
			component="img"
			src={src}
			alt={alt}
			sx={{
				display: "block",
				mx: "auto",
				maxWidth: "100%",
				maxHeight: { xs: "70vh", md: "80vh" },
				width: "auto",
				height: "auto",
				borderRadius: 2,
				my: 4,
			}}
		/>
	),
	hr: () => (
		<Box
			component="hr"
			sx={{
				border: 0,
				borderTop: "1px solid var(--mui-palette-divider)",
				my: 5,
			}}
		/>
	),
	table: ({ children }) => (
		<Box sx={{ my: 4, overflowX: "auto" }}>
			<Box
				component="table"
				sx={{
					width: "100%",
					borderCollapse: "collapse",
					fontSize: "0.9375rem",
					lineHeight: 1.6,
				}}>
				{children}
			</Box>
		</Box>
	),
	thead: ({ children }) => <Box component="thead">{children}</Box>,
	tbody: ({ children }) => <Box component="tbody">{children}</Box>,
	tr: ({ children }) => (
		<Box
			component="tr"
			sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
			{children}
		</Box>
	),
	th: ({ children }) => (
		<Box
			component="th"
			sx={{
				fontFamily: headingFontFamily,
				fontWeight: 600,
				textAlign: "left",
				verticalAlign: "top",
				p: 1.5,
				borderBottom: "2px solid var(--mui-palette-divider)",
			}}>
			{children}
		</Box>
	),
	td: ({ children }) => (
		<Box
			component="td"
			sx={{ verticalAlign: "top", p: 1.5 }}>
			{children}
		</Box>
	),
	code: CodeBlock,
	PullQuote,
	EmbeddedMap,
	Cta,
};
